import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'

import { getModelValue } from '@aytm/app/stats/lib/stat-page'

import { getImageAssetPath, IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT } from '@aytm/lib/common/assets'

import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

import style from '@aytm/app/stats/page/question-views/info-view/components/charts/horizontal-bars/styles/Block.module.scss'

// First paint, before the report column is read.
const DEFAULT_WIDTH = 814

// Info-view horizontal-bars chart. Replaces info_view/charts/horizontal_bars.coffee
// (#info-view-question-block-horizontal-bars-template + calcModelData). The legacy color
// theme only affected .fill via a no-op border-right rule, so it is not reproduced.

// 80px-capped thumbnail dimensions, mirroring the template's aspect-ratio math.
const thumbDims = image => {
  const s = 80
  let w = image.original.width
  let h = image.original.height
  if (s < w || s < h) {
    const r = w / h
    if (1 === r) {
      w = s
      h = s
    } else if (r > 1) {
      w = s
      h = s / r
    } else {
      h = s
      w = s * r
    }
  }
  return { w, h }
}

const sortAvs = (list, pct, order) => {
  const arr = list.slice()
  switch (order) {
    case 'idesc':
      return arr.sort((a, b) => b.position - a.position)
    case 'vasc':
      return arr.sort((a, b) => pct[a.position] + a.position / 100000 - (pct[b.position] + b.position / 100000))
    case 'vdesc':
      return arr.sort((a, b) => pct[b.position] + b.position / 100000 - (pct[a.position] + a.position / 100000))
    case 'iasc':
    default:
      return arr.sort((a, b) => a.position - b.position)
  }
}

const Block = ({ title, label, colors, order, colorTheme, palette, answer, answerVariants, percentDecimals }) => {
  // the bars are laid out across the report column, which the hook reads for itself
  const { width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  // the figure the reader compares bars by, so it follows the report rather than keeping its own
  const decimals = percentDecimals ?? getModelValue('percentDecimals') ?? 1

  const { avs, min, max, step, withImages, bw, pct } = useMemo(() => {
    const list = answerVariants || []
    const withImages = list.some(av => av.image && av.image.original)

    const pct = {}
    let min = 100
    let max = 0
    list.forEach(av => {
      const p = ('object' == typeof answer && answer[`a${av.position}`]?.percents) || 0
      pct[av.position] = p
      min = Math.min(min, p)
      max = Math.max(max, p)
    })
    const rangeStart = 'object' == typeof answer ? answer.rangeStart : undefined
    const rangeEnd = 'object' == typeof answer ? answer.rangeEnd : undefined
    min = undefined != rangeStart ? rangeStart || 0 : Math.max(0, min - 5)
    max = undefined != rangeEnd ? rangeEnd || 100 : Math.min(100, max + 5)

    return {
      avs: sortAvs(list, pct, order),
      min,
      max,
      step: (max - min) / 10,
      withImages,
      bw: width - (withImages ? 257 : 167),
      pct
    }
  }, [answerVariants, answer, order, width])

  const isLoading = 'loading' === answer

  // Single container theme class, mirroring the legacy setColorTheme (palette -> custom).
  const themeClass = null != palette ? 'color-theme-custom' : `color-theme-${colorTheme}`

  return (
    <BlockWrapper>
      <div className={style.component}>
        <div className={style.title}>{title}</div>
        <div className={style.body}>
          <div className={classNames(style.avBars, themeClass)}>
            {avs.map((av, i) => {
              const p = pct[av.position]
              const barWidth = ((p - min) * bw) / (max - min)
              const dims = av.image && av.image.original ? thumbDims(av.image) : null
              return (
                <div key={av.position} className={style.row}>
                  {withImages ? (
                    <div className={classNames(style.cell, style.image)}>
                      {dims ? (
                        <div className={style.thumbnail} style={{ width: `${dims.w}px`, height: `${dims.h}px` }}>
                          <img
                            alt=""
                            src={getImageAssetPath({
                              path: av.image.original.path,
                              width: 80,
                              height: 80,
                              filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
                              backgroundColor: 'white'
                            })}
                          />
                        </div>
                      ) : (
                        ' '
                      )}
                    </div>
                  ) : null}
                  <div className={classNames(style.cell, style.bar)}>
                    <div className={style.barTitle}>{av.text}</div>
                    <div className={style.progressStatus}>
                      {isLoading ? null : (
                        <>
                          <span
                            // the palette classes count from 1, so the first bar takes the theme's first colour
                            className={classNames(style.fill, `color-${colors ? (i % 21) + 1 : 'no'}`)}
                            style={{ borderLeftWidth: `${barWidth}px` }}
                          />
                          <span className={classNames(style.progressText, { [style.colorBlack]: colors })}>
                            {p.toFixed(decimals)}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
            <div className={style.row}>
              {withImages ? <div className={classNames(style.cell, style.image)}>&nbsp;</div> : null}
              <div className={classNames(style.cell, style.scale)}>
                <div className={style.axis}>
                  {Array.from({ length: 11 }, (_, i) => {
                    const raw = min + i * step
                    const value = raw !== Math.round(raw) ? raw.toFixed(1) : raw
                    return <span key={i}>{value}%</span>
                  })}
                </div>
                <div className={style.scaleText}>{label}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlockWrapper>
  )
}

Block.displayName = 'Block'

Block.propTypes = {
  title: PropTypes.string,
  label: PropTypes.string,
  colors: PropTypes.bool,
  order: PropTypes.string,
  colorTheme: PropTypes.number,
  palette: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  answer: answerPropType,
  answerVariants: PropTypes.array
}

export default Block
